import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, Briefcase } from "lucide-react";
import { experience } from "../data/portfolio";

const ease = [0.16, 1, 0.3, 1];

function IntroSnap({ total }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], [180, -180]);
  const bgOpacity = useTransform(
    scrollYProgress,
    [0, 0.25, 0.75, 1],
    [0, 0.045, 0.045, 0]
  );
  const headerY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <div
      id="experience"
      ref={ref}
      data-snap-page
      className="relative h-[100svh] flex items-center overflow-hidden"
    >
      <motion.div
        aria-hidden
        style={{ y: bgY, opacity: bgOpacity }}
        className="pointer-events-none absolute right-[-3vw] top-1/2 -translate-y-1/2 font-display text-[28vw] md:text-[18vw] leading-none tracking-[-0.06em] text-ink select-none"
      >
        trajectory.
      </motion.div>

      <div className="relative max-w-[1400px] mx-auto w-full px-6 md:px-10">
        <motion.div style={{ y: headerY }} className="grid grid-cols-12">
          <div className="col-span-12 md:col-span-3">
            <p className="font-body text-[10px] md:text-xs tracking-[0.4em] uppercase text-ink-2">
              04 — Experience
            </p>
          </div>
          <div className="col-span-12 md:col-span-9 md:col-start-4 mt-6 md:mt-0">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, ease }}
              className="font-display text-4xl md:text-6xl lg:text-7xl tracking-[-0.03em] leading-[1.05] text-ink max-w-[20ch]"
            >
              {total} roles. <span className="text-metal">One trajectory.</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, ease, delay: 0.15 }}
              className="mt-6 font-body text-base md:text-lg text-ink-2 max-w-2xl leading-relaxed"
            >
              From classrooms in Kamloops to Deloitte's Toronto towers — five
              chapters of building software with people, for people. Scroll to
              walk the timeline, one role at a time.
            </motion.p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function RoleSnap({ role, idx, total }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], [200, -200]);
  const bgOpacity = useTransform(
    scrollYProgress,
    [0, 0.25, 0.75, 1],
    [0, 0.04, 0.04, 0]
  );
  const cardY = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const side = idx % 2 === 0 ? "right" : "left";

  return (
    <div
      ref={ref}
      data-snap-page
      data-testid={`experience-${idx}`}
      className="relative h-[100svh] flex items-center overflow-hidden"
    >
      <motion.div
        aria-hidden
        style={{ y: bgY, opacity: bgOpacity }}
        className={`pointer-events-none absolute top-1/2 -translate-y-1/2 font-display text-[44vw] md:text-[26vw] lg:text-[22vw] leading-none tracking-[-0.06em] text-ink select-none ${
          side === "right" ? "right-[-3vw]" : "left-[-3vw]"
        }`}
      >
        {String(idx + 1).padStart(2, "0")}
      </motion.div>

      <div className="relative w-full max-w-[1400px] mx-auto px-6 md:px-10">
        <motion.div
          style={{ y: cardY }}
          className="grid grid-cols-12 gap-x-8 gap-y-8 items-start md:items-center"
        >
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease }}
            className="col-span-12 md:col-span-4"
          >
            <div className="flex items-center gap-3">
              <span className="font-body text-[10px] md:text-xs tracking-[0.4em] uppercase text-ink-2">
                Role {String(idx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
              </span>
            </div>
            <div className="mt-6 relative pl-6 border-l border-line/15">
              <span className="absolute -left-[5px] top-1.5 block w-2.5 h-2.5 rounded-full bg-ink" />
              <p className="font-body text-xs md:text-sm tracking-[0.25em] uppercase text-ink-metal">
                {role.period}
              </p>
              <p className="mt-2 font-body text-xs md:text-sm text-ink-3 inline-flex items-center gap-1.5">
                <MapPin className="w-3 h-3" /> {role.location}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease, delay: 0.1 }}
            className="col-span-12 md:col-span-8"
          >
            <div className="flex items-start gap-3 mb-4">
              <span className="mt-1.5 grid place-items-center w-8 h-8 rounded-full bg-line/[0.04] border border-line/10 text-ink-metal">
                <Briefcase className="w-3.5 h-3.5" strokeWidth={1.5} />
              </span>
              <div>
                <h3 className="font-display text-2xl md:text-4xl lg:text-5xl tracking-[-0.025em] text-ink leading-[1.05]">
                  {role.role}
                </h3>
                <p className="mt-1.5 font-display text-base md:text-xl text-ink-metal">
                  {role.company}
                </p>
              </div>
            </div>

            <ul className="mt-6 space-y-3 max-w-3xl">
              {role.highlights.map((h, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: 12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.55, ease, delay: 0.25 + i * 0.08 }}
                  className="font-body text-sm md:text-base text-ink-2 leading-relaxed pl-5 relative before:content-[''] before:absolute before:left-0 before:top-[0.7em] before:w-2.5 before:h-px before:bg-ink-3"
                >
                  {h}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}

export default function Experience() {
  return (
    <section data-testid="experience-section" className="relative bg-surface">
      <IntroSnap total={experience.length} />
      {experience.map((r, i) => (
        <RoleSnap key={r.role} role={r} idx={i} total={experience.length} />
      ))}
    </section>
  );
}
